/**
 * Editor color scheme presets (Slice 1) — syntax-highlighting TOKEN colors
 * only. Surface colors (background, gutter, minimap, selection, widgets) are
 * owned by `monacoTheme.ts` / `JAM_MONACO_LIGHT` / `JAM_MONACO_DARK` and never
 * change here — that is the CH-10 seam fix and must not regress.
 *
 * Each scheme ships a light AND a dark `EditorColorSchemeTokenColors` table;
 * the active app theme mode picks the variant. `buildMonacoTokenRules` turns
 * one table into the `ITokenThemeRule[]` Monaco's `defineTheme` expects.
 */

/**
 * The token classes the ACTIVE `sql` tokenizer emits — see the "which
 * tokenizer is actually active" note on `buildMonacoTokenRules` below.
 * `predefined` (builtin functions: COUNT, SUM, DATEADD, GETDATE, …) is part
 * of that set even though the app's own Monarch tokenizer never runs.
 */
export type EditorColorSchemeTokenColors = {
  keyword: string;
  identifier: string;
  number: string;
  string: string;
  'string.double': string;
  operator: string;
  comment: string;
  'comment.quote': string;
  predefined: string;
};

export type EditorColorSchemeId = 'default' | 'high-contrast' | 'github' | 'solarized' | 'atom-one';

/**
 * A scheme selection as stored/surfaced by Settings → UI → Editor colors:
 * one of the built-in presets above, or `'custom'` (Slice 3) — the user's own
 * token-color table, edited via `core/theme/customEditorColors.ts` and the
 * Custom panel. `'custom'` is deliberately NOT part of `EditorColorSchemeId`
 * itself: `EDITOR_COLOR_SCHEMES` and this file's WCAG floor tests iterate
 * `EditorColorSchemeId` only, and a user's custom colors carry no floor.
 */
export type EditorColorSelection = EditorColorSchemeId | 'custom';

export interface EditorColorScheme {
  id: EditorColorSchemeId;
  label: string;
  light: EditorColorSchemeTokenColors;
  dark: EditorColorSchemeTokenColors;
}

export const EDITOR_COLOR_SCHEMES: EditorColorScheme[] = [
  {
    id: 'default',
    label: 'Default',
    // Stock Monaco `vs` / `vs-dark` token colors, stated explicitly so the
    // scheme-switch code path is exercised even when the user never leaves
    // the default. Visually identical to `inherit: true` except for the
    // light `operator` (see its note below): stock's slate fails this file's
    // WCAG floor on `#ffffff`, so it is darkened within the same hue.
    dark: {
      keyword: '#569CD6',
      identifier: '#D4D4D4',
      string: '#CE9178',
      'string.double': '#CE9178',
      number: '#B5CEA8',
      // The bundled `vs-dark` base theme's own `operator.sql` value
      // (monaco-editor/esm/vs/editor/standalone/common/themes.js:116), so
      // operators stay a distinct slate instead of collapsing into
      // `identifier`. See the light variant's note for why only that side
      // deviates from stock.
      operator: '#778899',
      comment: '#6A9955',
      'comment.quote': '#6A9955',
      // The bundled `vs-dark` base theme's own `predefined.sql` value
      // (monaco-editor/esm/vs/editor/standalone/common/themes.js:118) —
      // stated explicitly so Default stays visually identical to today.
      predefined: '#FF00FF',
    },
    light: {
      keyword: '#0000FF',
      identifier: '#000000',
      string: '#A31515',
      'string.double': '#A31515',
      number: '#098658',
      // Darkened from the bundled `vs` theme's own `operator.sql` value
      // (`#778899`, same file line 55 — measured 3.64:1 on `#ffffff`, under
      // this file's 4.2:1 floor) to `#647585` (4.75:1): the same slate hue
      // and saturation (210°/14%), lightness 53% → 46%. Only the LIGHT
      // variant needs this nudge; `#778899` clears the floor on the dark
      // canvas (4.97:1) and is kept verbatim there.
      operator: '#647585',
      comment: '#008000',
      'comment.quote': '#008000',
      // The bundled `vs` base theme's own `predefined.sql` value (same file, line 57).
      predefined: '#C700C7',
    },
  },
  {
    id: 'high-contrast',
    label: 'High contrast',
    dark: {
      keyword: '#4FC1FF',
      identifier: '#F0F6FC',
      string: '#FFA657',
      'string.double': '#FFA657',
      number: '#7EE787',
      operator: '#D2A8FF',
      comment: '#8FBF8F',
      'comment.quote': '#8FBF8F',
      predefined: '#FF9BE3',
    },
    light: {
      keyword: '#0033CC',
      identifier: '#111827',
      string: '#A31515',
      'string.double': '#A31515',
      number: '#0A5A10',
      operator: '#5A2FA8',
      comment: '#2F5E2F',
      'comment.quote': '#2F5E2F',
      predefined: '#99009B',
    },
  },
  {
    id: 'github',
    label: 'GitHub',
    // Primer syntax colors.
    dark: {
      keyword: '#FF7B72',
      identifier: '#C9D1D9',
      string: '#A5D6FF',
      'string.double': '#A5D6FF',
      number: '#79C0FF',
      operator: '#FF7B72',
      comment: '#8B949E',
      'comment.quote': '#8B949E',
      predefined: '#D2A8FF', // canonical Primer function purple
    },
    light: {
      keyword: '#CF222E',
      identifier: '#24292F',
      string: '#0A3069',
      'string.double': '#0A3069',
      number: '#0550AE',
      operator: '#CF222E',
      comment: '#6E7781',
      'comment.quote': '#6E7781',
      predefined: '#8250DF', // canonical Primer function purple
    },
  },
  {
    id: 'solarized',
    label: 'Solarized',
    // Canonical Solarized accents kept, but several values are deliberately
    // darkened/brightened from canonical — canonical Solarized is ~3:1 on our
    // pure-white/navy canvases (it was designed for its own muted base
    // tones, not `#ffffff` / `#0f1624`), which fails this file's WCAG floor.
    dark: {
      keyword: '#859900',
      identifier: '#839496',
      string: '#2AA198',
      'string.double': '#2AA198',
      number: '#DD4A97', // brightened from canonical #D33682
      operator: '#93A1A1',
      comment: '#657B83', // canonical base00, not base01 (#586E75) — more legible on our darker canvas
      'comment.quote': '#657B83',
      predefined: '#268BD2', // canonical function blue
    },
    light: {
      keyword: '#6A7A00', // darkened from canonical #859900
      identifier: '#586E75',
      string: '#1F7A73', // darkened from canonical #2AA198
      'string.double': '#1F7A73',
      number: '#B0246E', // darkened from canonical #D33682
      operator: '#657B83',
      comment: '#728585', // darkened from canonical #93A1A1
      'comment.quote': '#728585',
      predefined: '#2276B5', // darkened from canonical #268BD2 for contrast on white
    },
  },
  {
    id: 'atom-one',
    label: 'Atom One',
    // One Dark / One Light.
    dark: {
      keyword: '#C678DD',
      identifier: '#ABB2BF',
      string: '#98C379',
      'string.double': '#98C379',
      number: '#D19A66',
      operator: '#56B6C2',
      comment: '#6E7787', // lifted from canonical #5C6370 for contrast
      'comment.quote': '#6E7787',
      predefined: '#61AFEF', // canonical
    },
    light: {
      keyword: '#A626A4',
      identifier: '#383A42',
      string: '#418241', // darkened from canonical #50A14F
      'string.double': '#418241',
      number: '#986801',
      // Darkened from canonical #0184BC (measured 4.18:1 on #ffffff, just
      // under this file's 4.2:1 floor) to #0183BA (4.24:1) — a one-point
      // nudge in the green/blue channels, same hue.
      operator: '#0183BA',
      comment: '#696C77', // darkened from canonical #A0A1A7
      'comment.quote': '#696C77',
      // Darkened from canonical #4078F2 (measured 4.05:1 on #ffffff, under
      // this file's 4.2:1 floor) to #3A6DE0 (4.73:1, same hue).
      predefined: '#3A6DE0',
    },
  },
];

const SCHEME_IDS = new Set<string>(EDITOR_COLOR_SCHEMES.map((scheme) => scheme.id));

/** Guards imports of settings bundles (other machines, hand-edited storage). */
export function normalizeEditorColorScheme(value: unknown): EditorColorSelection {
  if (value === 'custom') return 'custom';
  if (typeof value === 'string' && SCHEME_IDS.has(value)) {
    return value as EditorColorSchemeId;
  }
  return 'default';
}

export function getEditorColorScheme(id: EditorColorSchemeId): EditorColorScheme {
  return EDITOR_COLOR_SCHEMES.find((scheme) => scheme.id === id) ?? EDITOR_COLOR_SCHEMES[0];
}

/** Monaco `ITokenThemeRule`-shaped rule (no monaco-editor import needed here). */
export interface MonacoTokenRule {
  token: string;
  foreground: string;
}

function stripHash(hex: string): string {
  return hex.startsWith('#') ? hex.slice(1) : hex;
}

/**
 * Builds the full `rules` array Monaco's `defineTheme` needs for one
 * scheme+mode's token colors — plus three collision guards and three PL/SQL
 * mappings that are NOT part of the public `EditorColorSchemeTokenColors`
 * shape:
 *
 * WHICH TOKENIZER IS ACTUALLY ACTIVE FOR THE `sql` LANGUAGE — this app's own
 * Monarch tokenizer (`sqlMonarchTokenizer.ts`) never installs.
 * `registerSqlLanguage` only registers it when no `sql` language is already
 * registered, but monaco-editor's bundled basic-languages contribution
 * registers `sql` first — every editor is actually colored by MONACO'S
 * BUILT-IN `sql` tokenizer. That tokenizer's emitted classes are `keyword`,
 * `identifier`, `identifier.quote`, `operator`, `predefined`, `number`,
 * `string`, `comment`, `comment.quote`, `delimiter` (this is also why COUNT
 * / SUM / DATEADD / GETDATE come out as `predefined`, not `keyword`, even
 * though the app's OWN keyword list includes COUNT/SUM — that list is never
 * consulted). `registerSqlLanguage` itself is out of scope for this slice.
 *
 * `string.sql` / `operator.sql` / `predefined.sql` guards — Monarch appends
 * `tokenPostfix` (`.sql` for the builtin `sql` language) to every emitted
 * token. The bundled `vs` / `vs-dark` base themes
 * (`monaco-editor/esm/vs/editor/standalone/common/themes.js`) hardcode
 * `string.sql` (`#FF0000`), `operator.sql` (`#778899`), and `predefined.sql`
 * (`#C700C7` light / `#FF00FF` dark, lines 57/118) as rules MORE SPECIFIC
 * than the bare `string` / `operator` / `predefined` rules below, and
 * Monaco's token-color trie resolves the most specific match — so without
 * these three overrides, every non-default scheme would silently leak
 * Monaco's stock red strings, gray operators, and magenta builtin functions.
 *
 * `type.plsql` / `predefined.plsql` / `variable.plsql` — token classes the
 * PL/SQL tokenizer (`plsqlMonarchTokenizer.ts`) emits beyond the shared SQL
 * set (PL/SQL's own Monarch tokenizer DOES install — `registerPlSqlLanguage`
 * registers a distinct `plsql` language id with no bundled built-in to lose
 * to). `type` (Oracle types: NUMBER, VARCHAR2, DATE, …) reads as a
 * reserved-word-like token, mapped to the keyword color. `predefined`
 * (builtins: NVL, TO_CHAR, SYSDATE, DBMS_OUTPUT, …) is a function name, so it
 * maps to the same `predefined` color as the builtin `sql` tokenizer's own
 * COUNT/SUM/DATEADD/GETDATE above — one function color regardless of which
 * tokenizer is active. `variable` (bind vars `:name`, substitution `&var`)
 * is a plain named reference, mapped to the identifier color. (`keyword`
 * plus tokenPostfix `.plsql` resolves as `keyword.plsql`, which has no
 * colliding base rule and already falls back correctly to the bare
 * `keyword` rule below — no explicit override needed.)
 */
export function buildMonacoTokenRules(colors: EditorColorSchemeTokenColors): MonacoTokenRule[] {
  const base: MonacoTokenRule[] = (Object.keys(colors) as Array<keyof EditorColorSchemeTokenColors>).map(
    (token) => ({ token, foreground: stripHash(colors[token]) }),
  );

  return [
    ...base,
    { token: 'string.sql', foreground: stripHash(colors.string) },
    { token: 'operator.sql', foreground: stripHash(colors.operator) },
    { token: 'predefined.sql', foreground: stripHash(colors.predefined) },
    { token: 'type.plsql', foreground: stripHash(colors.keyword) },
    { token: 'predefined.plsql', foreground: stripHash(colors.predefined) },
    { token: 'variable.plsql', foreground: stripHash(colors.identifier) },
  ];
}

/** The 4 preview swatches (keyword, string, number, comment) for a Settings UI select item. */
export function getEditorColorSchemeSwatches(schemeId: EditorColorSchemeId, isDark: boolean): string[] {
  const scheme = getEditorColorScheme(schemeId);
  const colors = isDark ? scheme.dark : scheme.light;
  return [colors.keyword, colors.string, colors.number, colors.comment];
}
